import React, {useState, FocusEvent, ChangeEvent, useCallback, useEffect, useRef} from 'react';
import {Text, TextVariants} from '../Text';
import {InputStateTypes} from '@/interface';
import {cn} from '@/utils/common_functions';
import {useSelector} from 'react-redux';
import {appCountries} from '@/services/redux/selectors';
import {Country} from '@/interface';
import {Icon} from '../Icon';
import {useClickOutside} from '@/hooks';
import ReactCountryFlag from 'react-country-flag';
import { defaultCountryId } from '@/constants';

interface CurrencyInputProps {
  label?: string;
  value: string;
  currency?: number | null;
  name?: string;
  onChange: (amount: string, currencyId: number) => void;
  disabled?: boolean;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  error?: string;
  touched?: boolean;
  placeholder?: string;
  required?: boolean;
  className?: string;
  wrapperClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  showOptionalLabel?: boolean;
  lockCurrency?: boolean;
  maxValue?: number;
}

export const CurrencyInput: React.FC<CurrencyInputProps> = props => {
  const {
    value,
    className = '',
    disabled = false,
    error = '',
    label = '',
    name = '',
    onBlur,
    onChange,
    onFocus,
    placeholder = '',
    required = false,
    touched = false,
    wrapperClassName = '',
    labelClassName = '',
    inputClassName = '',
    showOptionalLabel = false,
    currency = null,
    lockCurrency = false,
    maxValue,
  } = props;

  const [inputState, setInputState] = useState<InputStateTypes>('default');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<Country | null>(null);
  const dropdownRef = useClickOutside<HTMLDivElement>(() => {
    setIsDropdownOpen(false);
  });
  const countries = useSelector(appCountries);

  // Initialize selected currency based on currency prop
  useEffect(() => {
    if (countries.length > 0) {
      const foundCurrency = countries.find(c => {
        if (!currency){
          return c.id === defaultCountryId;
        }
        return c.id === currency;
      });
      if (foundCurrency) {
        setSelectedCurrency(foundCurrency);
      }
    }
  }, [currency, countries]);

  /**
   * onChange handler for input
   */
  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    let text = e.target.value;

    // Allow only valid decimal format
    const decimalValue = text.replace(/[^\d.]/g, ''); // Remove non-digit and non-dot

    // Prevent multiple decimal points
    const parts = decimalValue.split('.');
    let formattedValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : decimalValue;

    // Check max value if provided
    if (maxValue !== undefined && maxValue !== null && formattedValue) {
      const numValue = parseFloat(formattedValue);
      if (numValue > maxValue) {
        formattedValue = maxValue.toString();
      }
    }

    if (selectedCurrency) {
      onChange(formattedValue, selectedCurrency.id);
    }
  }

  /**
   * Handle currency selection from dropdown
   */
  const handleCurrencySelect = (country: Country) => {
    setSelectedCurrency(country);
    setIsDropdownOpen(false);
    onChange(value, country.id);
  };

  /**
   * Toggle dropdown
   */
  const toggleDropdown = () => {
    if (!lockCurrency && !disabled) {
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  /**
   * Update input state utility function
   */
  const updateInputState = useCallback(
    (state: InputStateTypes) => {
      if (disabled) {
        return;
      }
      setInputState(state);
    },
    [disabled],
  );

  /**
   * onBlur handler wrapper
   */
  function onBlurHandler(e: FocusEvent<HTMLInputElement>): void {
    updateInputState(error && touched ? 'error' : 'default');
    onBlur?.(e);
  }

  /**
   * onFocus handler wrapper
   */
  function onFocusHandler(e: FocusEvent<HTMLInputElement>): void {
    updateInputState('focus');
    onFocus?.(e);
  }

  useEffect(() => {
    if (touched && error) {
      updateInputState('error');
    } else {
      updateInputState('default');
    }
  }, [error, touched, updateInputState]);

  return (
    <div className={cn('flex flex-col gap-1 lg:gap-2', className)}>
      {label && (
        <Text className={cn('!font-FigtreeSemiBold inline-flex gap-1 text-ui_blue', labelClassName)} variant="body">
          {label}
          {required && <p className="text-rejection">*</p>}
          {showOptionalLabel && <p className="font-FigtreeLight text-xs self-center mt-1">(optional)</p>}
        </Text>
      )}
      <div
        className={cn(
          'border-2 flex border-ui_input_border relative text-base rounded-lg items-center disabled:cursor-not-allowed',
          inputState === 'error' && 'border-rejection',
          inputState === 'focus' && 'border-black',
          disabled && 'border-ui_disabled',
          TextVariants.body,
          wrapperClassName,
        )}>
        {/* Currency Selector Button */}
        <div className="relative self-stretch h-full flex" ref={dropdownRef}>
          <button
            type="button"
            onClick={toggleDropdown}
            disabled={lockCurrency || disabled}
            className={cn(
              'flex items-center gap-2 rounded-l-lg justify-center px-3 py-2 lg:px-4 border-r border-ui_input_border bg-gray-50 font-FigtreeMedium ',
              (lockCurrency || disabled) && 'cursor-not-allowed text-ui_disabled',
              !lockCurrency && !disabled && 'hover:bg-gray-50',
            )}>
            <Text variant="small" className='text-nowrap'>{selectedCurrency?.currency_code || 'INR'} ({selectedCurrency?.currency_symbol || '₹'})</Text>
            {!lockCurrency && <div className='triangle-down'/>}
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && !lockCurrency && (
            <div className="absolute top-full left-0 mt-1 w-[200px] bg-white border-ui_input_border shadow-lg z-50 max-h-[250px] overflow-y-auto">
              {countries.map(country => (
                <button
                  key={country.id}
                  type="button"
                  onClick={() => handleCurrencySelect(country)}
                  className={cn(
                    'w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-2 border-b border-gray-100 last:border-b-0',
                    selectedCurrency?.id === country.id && 'bg-grey-100',
                  )}>
                  <ReactCountryFlag
                    countryCode={country.country_code}
                    svg
                    style={{
                      width: '1em',
                      height: '1.2em',
                      borderRadius: 4,
                    }}
                  />
                  <Text variant="tiny">{country.currency_code}</Text>
                  <Text variant="tiny" className="text-ui_input_border_dark">
                    ({country.currency_symbol})
                  </Text>
                </button>
              ))}
            </div>
          )}
        </div>
        {/* <span className="text-sm text-gray-500">({selectedCurrency?.currency_symbol || '₹'})</span> */}
        {/* Amount Input */}
        <input
          type="text"
          name={name}
          value={value}
          onChange={handleChange}
          onFocus={onFocusHandler}
          onBlur={onBlurHandler}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            'px-3 py-2 lg:px-4 debug border-none outline-none bg-transparent grow w-full font-FigtreeMedium placeholder:text-ui_input_border_dark',
            disabled && 'text-ui_disabled cursor-not-allowed',
            inputClassName,
          )}
        />
      </div>
      {!disabled && touched && error && <Text className="text-rejection text-sm">{error}</Text>}
    </div>
  );
};
